import { useEffect, useRef, useState } from "react";
import { ChevronDown, Menu, Search, ShoppingBag, User, X } from "lucide-react";
import { HOME, ACCOUNT } from "../constants/testIds";
import { NAV_ITEMS as BASE_NAV_ITEMS, BUSINESS } from "../constants/business";
import { useInStock } from "../components/storefront/InStock";
import { useAccount } from "../state/account";
import { useCart } from "../state/cart";
import SearchOverlay from "../components/search/SearchOverlay";

function ChevronMark({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M3 5 L12 12 L3 19" stroke="currentColor" strokeWidth="2.4" strokeLinecap="square" strokeLinejoin="miter"/>
      <path d="M11 5 L20 12 L11 19" stroke="currentColor" strokeWidth="2.4" strokeLinecap="square" strokeLinejoin="miter"/>
    </svg>
  );
}

function DiscordGlyph({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3.2a.075.075 0 0 0-.079.037c-.34.605-.719 1.396-.984 2.013a18.27 18.27 0 0 0-5.487 0 12.51 12.51 0 0 0-.997-2.013.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.369a.07.07 0 0 0-.032.027C.533 9.046-.32 13.579.099 18.057a.083.083 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.15 14.15 0 0 0 1.226-1.994.076.076 0 0 0-.042-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.009c.12.099.246.198.372.292a.077.077 0 0 1-.006.127c-.598.35-1.22.647-1.873.891a.077.077 0 0 0-.041.107c.36.7.772 1.363 1.225 1.993a.076.076 0 0 0 .084.028 19.834 19.834 0 0 0 6.002-3.03.077.077 0 0 0 .031-.055c.5-5.177-.838-9.674-3.548-13.66a.061.061 0 0 0-.031-.028zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.334.956-2.419 2.157-2.419 1.21 0 2.176 1.094 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.334.955-2.419 2.157-2.419 1.21 0 2.176 1.094 2.157 2.42 0 1.333-.947 2.418-2.157 2.418Z" />
    </svg>
  );
}

export default function MainHeader({ cartCount: cartCountProp }) {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const acctRef = useRef(null);
  const { status, customer } = useAccount();
  const { totalQuantity, openDrawer } = useCart();
  const cartCount = typeof cartCountProp === "number" ? cartCountProp : totalQuantity || 0;
  // "IN STOCK" replaces "THE LAB" in the nav while anything is on the
  // shelf; falls back to THE LAB when the in-stock feed is empty.
  const { count: inStockCount } = useInStock(1);
  const NAV_ITEMS = inStockCount > 0
    ? BASE_NAV_ITEMS.map((n) => (n.href === "/operator-lab" ? { label: "IN STOCK", href: "/in-stock" } : n))
    : BASE_NAV_ITEMS;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!acctOpen) return;
    const onDoc = (e) => {
      if (acctRef.current && !acctRef.current.contains(e.target)) setAcctOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [acctOpen]);

  const firstName = (customer?.first_name || customer?.email || "").trim();
  const greetingLabel = firstName ? firstName.split(/[\s@]/)[0].toUpperCase() : "";

  return (
    <header
      data-testid={HOME.mainHeader}
      className={
        "sticky top-0 z-40 bg-white h-[72px] transition-shadow " +
        (scrolled ? "border-b border-[#1F1F1F] shadow-[0_1px_0_#1F1F1F]" : "border-b border-transparent")
      }
    >
      <div className="mx-auto max-w-[1440px] px-6 h-full flex items-center gap-4 md:gap-6">
        <button
          type="button"
          aria-label="Open menu"
          onClick={() => setDrawerOpen(true)}
          className="touch-target lg:hidden inline-flex items-center justify-center w-12 h-12 text-[#0A0A0A] hover:text-[#FF9F1C] transition-colors"
          data-testid="main-header-hamburger"
        >
          <Menu className="h-6 w-6" />
        </button>

        <a
          data-testid={HOME.brandWordmark}
          href="/"
          className="flex items-center gap-2 text-[#0A0A0A] md:mr-6 shrink-0"
          aria-label="Loadout — go to homepage"
        >
          <span className="font-display text-[28px] leading-none">LOADOUT</span>
        </a>

        {/* Desktop nav — visible ≥1024px only, per spec.
            Three-tier sizing so the full 10-item bar fits every width:
            lg (1024–1279): gap-4 / 11px — covers 125% zoom (~1152px).
            xl (1280–1535): gap-5 / 12px — laptops at 1366/1440.
            2xl (1536+):    gap-8 / 13px — full-size desktop. */}
        <nav className="hidden lg:flex items-center gap-4 xl:gap-5 2xl:gap-8 flex-1 min-w-0">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.label}
              href={item.href}
              data-testid={HOME.navItem}
              className="font-sans text-[11px] xl:text-[12px] 2xl:text-[13px] font-semibold uppercase tracking-[0.02em] text-[#0A0A0A] hover:text-[#FF9F1C] transition-colors whitespace-nowrap"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3 md:gap-4 text-[#0A0A0A] shrink-0">
          <button
            type="button"
            data-testid={HOME.searchButton}
            aria-label="Search"
            onClick={() => setSearchOpen((v) => !v)}
            className="hover:text-[#FF9F1C] transition-colors"
          >
            <Search className="h-5 w-5" strokeWidth={1.8} />
          </button>

          {/* Account slot — dropdown when authed, link when guest */}
          <div ref={acctRef} className="relative">
            {status === "authed" && customer ? (
              <>
                <button
                  type="button"
                  data-testid={ACCOUNT.headerDropdownToggle}
                  aria-haspopup="menu"
                  aria-expanded={acctOpen}
                  onClick={() => setAcctOpen((v) => !v)}
                  className="flex items-center gap-1.5 font-sans text-[13px] font-semibold uppercase tracking-[0.02em] text-[#0A0A0A] hover:text-[#FF9F1C] transition-colors"
                >
                  {/* Below xl: icon only — the "Hi, NAME" label crowds the
                      bar and pushes the cart/discord icons off the right edge. */}
                  <User className="h-5 w-5 xl:hidden" strokeWidth={1.8} />
                  <span data-testid={ACCOUNT.headerGreeting} className="hidden xl:inline">Hi, {greetingLabel}</span>
                  <ChevronDown className={"hidden xl:block h-4 w-4 transition-transform " + (acctOpen ? "rotate-180" : "")} />
                </button>
                {acctOpen && (
                  <div
                    role="menu"
                    data-testid={ACCOUNT.headerDropdownMenu}
                    className="absolute right-0 top-full mt-2 min-w-[200px] bg-white border border-[#1F1F1F] shadow-lg z-50"
                  >
                    <a
                      href="/account/index"
                      data-testid={ACCOUNT.headerAccountItem}
                      className="block px-4 py-3 font-sans text-[13px] font-semibold uppercase tracking-[0.02em] text-[#0A0A0A] hover:bg-[#F5F5F5] hover:text-[#FF9F1C] transition-colors"
                    >
                      Account
                    </a>
                    <a
                      href="/account/orders"
                      data-testid={ACCOUNT.headerOrdersItem}
                      className="block px-4 py-3 font-sans text-[13px] font-semibold uppercase tracking-[0.02em] text-[#0A0A0A] hover:bg-[#F5F5F5] hover:text-[#FF9F1C] transition-colors"
                    >
                      Orders
                    </a>
                    <a
                      href="/account/logout"
                      data-testid={ACCOUNT.headerSignOutItem}
                      className="block px-4 py-3 font-sans text-[13px] font-semibold uppercase tracking-[0.02em] text-[#0A0A0A] border-t border-[#1F1F1F]/10 hover:bg-[#F5F5F5] hover:text-[#FF9F1C] transition-colors"
                    >
                      Sign out
                    </a>
                  </div>
                )}
              </>
            ) : (
              <a
                href="/account/login"
                data-testid={ACCOUNT.headerAccountLink}
                aria-label="Account"
                className="flex items-center gap-1.5 font-sans text-[13px] font-semibold uppercase tracking-[0.02em] text-[#0A0A0A] hover:text-[#FF9F1C] transition-colors"
              >
                <User className="h-5 w-5" strokeWidth={1.8} />
                <span className="hidden xl:inline">Account</span>
              </a>
            )}
          </div>

          <button
            type="button"
            data-testid={HOME.cartButton}
            onClick={openDrawer}
            aria-label="Open cart"
            className="relative hover:text-[#FF9F1C] transition-colors"
          >
            <ShoppingBag className="h-5 w-5" strokeWidth={1.8} />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#FF9F1C] text-[#0A0A0A] font-mono text-[10px] font-bold flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>
          <a
            data-testid={HOME.discordLink}
            href={BUSINESS.discord}
            target="_blank"
            rel="noreferrer noopener"
            aria-label="Discord"
            className="hover:text-[#FF9F1C] transition-colors"
          >
            <DiscordGlyph className="h-5 w-5" />
          </a>
        </div>
      </div>

      {searchOpen && (
        <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      )}

      {drawerOpen && (
        <div className="fixed inset-0 z-50 bg-[#0A0A0A] text-white flex flex-col p-6 gap-4 lg:hidden">
          <div className="flex items-center justify-between">
            <span className="font-display text-[22px]">LOADOUT</span>
            <button type="button" onClick={() => setDrawerOpen(false)} aria-label="Close menu">
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex flex-col gap-4 mt-6">
            {NAV_ITEMS.map((n) => (
              <a
                key={n.label}
                href={n.href}
                className="font-display text-[24px] text-white hover:text-[#FF9F1C]"
                onClick={() => setDrawerOpen(false)}
              >
                {n.label}
              </a>
            ))}
            <div className="pt-4 border-t border-white/10 flex flex-col gap-3">
              <a
                href="/track-order"
                className="font-mono text-[13px] uppercase tracking-[0.14em] text-white/70 hover:text-[#FF9F1C]"
                onClick={() => setDrawerOpen(false)}
              >
                Track Order
              </a>
              {status === "authed" ? (
                <>
                  <a
                    href="/account/index"
                    className="font-mono text-[13px] uppercase tracking-[0.14em] text-[#FF9F1C]"
                    onClick={() => setDrawerOpen(false)}
                  >
                    Account ({firstName ? firstName.split(/[\s@]/)[0] : "Signed In"})
                  </a>
                  <a
                    href="/account/orders"
                    className="font-mono text-[13px] uppercase tracking-[0.14em] text-white/70 hover:text-[#FF9F1C]"
                    onClick={() => setDrawerOpen(false)}
                  >
                    My Orders
                  </a>
                  <a
                    href="/account/logout"
                    className="font-mono text-[13px] uppercase tracking-[0.14em] text-white/50 hover:text-[#FF9F1C]"
                    onClick={() => setDrawerOpen(false)}
                  >
                    Sign Out
                  </a>
                </>
              ) : (
                <a
                  href="/account/login"
                  className="font-mono text-[13px] uppercase tracking-[0.14em] text-[#FF9F1C]"
                  onClick={() => setDrawerOpen(false)}
                >
                  Sign In / Account
                </a>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
